import { useTranslation } from 'react-i18next';
import { Button, Card, Pagination } from 'antd';
import { FileImageOutlined } from '@ant-design/icons';
import { Trash3Fill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import BasicCard from '../basicCard/BasicCard';
import PrimaryButton from '../buttons/PrimaryButton';

import { AggregatePresentationsProps } from '../../types/priceTypes';
import { PriceStatusEnum } from '../../enums/PriceStatusEnum';

const AggregatePresentations: React.FC<AggregatePresentationsProps> = ({
  products,
  finishRegistration,
  deleteAggregatePresentation,
  productCount,
  finishRegistrationClicked,
  isCitizen,
  triggeredFromTask,
  allTaskPricesRecorded,
}) => {
  const { t } = useTranslation('price');
  const [inprogress, setInprogress] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    if (finishRegistrationClicked > 0) {
      setInprogress(false);
    } else {
      setInprogress(true);
    }
  }, [finishRegistrationClicked]);

  useEffect(() => {
    const totalPages = Math.ceil((products?.length || 0) / pageSize);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [products, pageSize, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProducts = products?.slice(startIndex, startIndex + pageSize) || [];

  return (
    <>
      <div className="my-4 ml-0 space-y-4 rounded-lg xl:ml-4">
        <BasicCard>
          <div className="p-4 pb-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-black md:text-xl">
                {t('aggregatePresentations')}
              </h2>
              <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-gray-200 px-2 text-xs font-semibold text-gray-700">
                {productCount ?? 0}
              </span>
            </div>
            {products?.length === 0 && <p className="mb-4">{t('noPresentationsPlaceholder')}</p>}
            {products?.length > 0 && (
              <>
                <div className="mb-4 space-y-4">
                  {paginatedProducts.map((product, index) => (
                    <Card key={startIndex + index} className="rounded-lg border border-gray-200">
                      <div className="flex justify-between">
                        <h1 className="mb-4 text-lg font-bold">{product?.productName}</h1>
                        <div className="flex justify-between gap-2">
                          <Button
                            className="bg-red-400 hover:!bg-red-600"
                            size="small"
                            onClick={() => deleteAggregatePresentation(startIndex + index)}
                            disabled={!inprogress}
                          >
                            <Trash3Fill color="white" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm font-bold">
                          {t('price')} :{' '}
                          <span className="font-normal">
                            {product?.status === PriceStatusEnum.PRODUCTNOTFOUND ||
                            product?.isNotFound ? (
                              <span className="font-semibold text-red-500">
                                {t('priceNotFound')}
                              </span>
                            ) : (
                              product?.price
                            )}
                          </span>
                        </p>

                        {product?.photoOrInvoice?.length > 0 && (
                          <p className="text-sm font-normal">
                            <FileImageOutlined />
                            &nbsp;{product?.photoOrInvoice?.length} {t('photos')}
                          </p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
                {products?.length > 0 && (
                  <div className="my-4 flex w-full justify-center overflow-hidden py-1">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={products?.length}
                      onChange={handlePageChange}
                      size="small"
                      showSizeChanger={false}
                      responsive={true}
                    />
                  </div>
                )}
              </>
            )}
            {inprogress && !isCitizen && (!triggeredFromTask || allTaskPricesRecorded) && (
              <div className="hidden xl:block">
                <PrimaryButton
                  handleOnClick={finishRegistration}
                  width="100%"
                  height="40px"
                  hideBgGradient={true}
                  disabled={!products || products?.length === 0 || (productCount ?? 0) === 0}
                  className="flex items-center justify-center"
                >
                  {t('finishRegistration')}
                </PrimaryButton>
              </div>
            )}
          </div>
        </BasicCard>
      </div>
    </>
  );
};

export default AggregatePresentations;
